import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate, useNavigationType, useParams } from 'react-router-dom';
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc, deleteDoc } from "firebase/firestore";
import { auth, db } from './firebase';

import Welcome from './components/Welcome';
import Navbar from './components/layout/Navbar'; // YENİ YOL
import Dashboard from './pages/Dashboard/Dashboard';
import Hideout from './pages/Hideout/Hideout';
import Quests from './pages/Quests/Quests';
import KappaItems from './components/KappaItems';
import KappaPath from './pages/KappaPath/KappaPath';
import StoryGuide from './components/StoryGuide';
import Footer from './components/Footer';
import Seo from './components/Seo';
import QuestLanding from './components/QuestLanding';
import PrivacyPolicy from './pages/Legal/PrivacyPolicy';
import TermsOfService from './pages/Legal/TermsOfService';
import AboutUs from './pages/Legal/AboutUs';
import Contact from './pages/Legal/Contact';

import { DataProvider } from './context/DataContext';
import { LanguageProvider } from './context/LanguageContext';
import { normalizeProfile } from './utils/profile';
import {
  getActiveLanguage,
  getLanguageFromPath,
  isSupportedLanguage,
  localizeCurrentPath,
  localizedPath,
  localizedQuestPath,
  localizedStoryPath,
  routeIdFromPath,
  setActiveLanguage,
} from './utils/routes';

const LocaleRootRedirect = () => {
  const { locale } = useParams();
  const language = isSupportedLanguage(locale) && locale !== 'en' ? locale : 'en';
  return <Navigate to={localizedPath(language === 'en' ? 'dashboard' : 'quests', language)} replace />;
};

const LegacyStaticRedirect = ({ routeId, language }) => (
  <Navigate to={localizedPath(routeId, language)} replace />
);

const LegacyQuestRedirect = ({ language }) => {
  const { questSlug, locale } = useParams();
  const targetLanguage = language || (isSupportedLanguage(locale) ? locale : 'en');
  return <Navigate to={localizedQuestPath(questSlug, targetLanguage)} replace />;
};

const LegacyStoryRedirect = ({ language }) => {
  const { chapterId, locale } = useParams();
  const targetLanguage = language || (isSupportedLanguage(locale) ? locale : 'en');
  return <Navigate to={localizedStoryPath(chapterId, targetLanguage)} replace />;
};

const FallbackRedirect = () => {
  const location = useLocation();
  const language = getLanguageFromPath(location.pathname) || 'en';
  return <Navigate to={localizedPath('dashboard', language)} replace />;
};

const LocaleRoute = ({ children }) => {
  const { locale } = useParams();
  if (!isSupportedLanguage(locale)) return <FallbackRedirect />;
  return children;
};

const LocaleHistoryGuard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const didMountRef = React.useRef(false);

  useEffect(() => {
    const routeLanguage = getLanguageFromPath(location.pathname) || 'en';

    if (!didMountRef.current) {
      didMountRef.current = true;
      setActiveLanguage(routeLanguage);
      return;
    }

    const activeLanguage = getActiveLanguage();

    if (!activeLanguage) {
      setActiveLanguage(routeLanguage);
      return;
    }

    if (navigationType === 'POP' && activeLanguage !== routeLanguage) {
      navigate(localizeCurrentPath(location.pathname, activeLanguage), { replace: true });
    }
  }, [location.pathname, navigate, navigationType]);

  return null;
};

function AppShell({ profile, onUpdate, onWelcomeComplete, onReset, backgroundClass }) {
  const location = useLocation();
  const currentLang = getLanguageFromPath(location.pathname) || profile?.settings?.language || 'en';
  const isPublicLegalPage = ['privacy-policy', 'terms-of-service', 'about', 'contact'].includes(routeIdFromPath(location.pathname));

  return (
    <LanguageProvider initialLanguage={currentLang}>
      <DataProvider>
        <Seo />
        <LocaleHistoryGuard />
        <div className={`min-h-screen ${backgroundClass} text-white font-sans transition-all duration-700 ease-in-out relative`}>
          <div className="absolute inset-0 bg-dot-pattern pointer-events-none z-0"></div>
          <div className="bg-scanlines"></div>

          <div className={!profile && !isPublicLegalPage ? "filter blur-md pointer-events-none" : ""}>
            <Navbar
              profile={profile || { nickname: 'Guest' }}
              onReset={onReset}
              onUpdate={onUpdate}
            />

            <div className="relative">
              <div className="fixed inset-0 bg-black/20 z-0 pointer-events-none" />
              <div className="relative z-10 flex flex-col min-h-[calc(100vh-64px)]">
                <div className="flex-1">
                  <Routes>
                    <Route path="/" element={<Dashboard profile={profile} onUpdate={onUpdate} />} />
                    <Route path="/dashboard" element={<Dashboard profile={profile} onUpdate={onUpdate} />} />
                    <Route path="/hideout" element={<Hideout profile={profile} onUpdate={onUpdate} />} />
                    <Route path="/quests" element={<Quests profile={profile} onUpdate={onUpdate} />} />
                    <Route path="/kappa-items" element={<KappaItems profile={profile} onUpdate={onUpdate} />} />
                    <Route path="/kappa-path" element={<KappaPath profile={profile} onUpdate={onUpdate} />} />
                    <Route path="/story" element={<StoryGuide profile={profile} onUpdate={onUpdate} />} />
                    <Route path="/story/:chapterId" element={<StoryGuide profile={profile} onUpdate={onUpdate} />} />
                    <Route path="/quest/:questSlug" element={<QuestLanding />} />
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                    <Route path="/terms-of-service" element={<TermsOfService />} />
                    <Route path="/about" element={<AboutUs />} />
                    <Route path="/contact" element={<Contact />} />

                    <Route path="/guide/:chapterId" element={<LegacyStoryRedirect language="en" />} />

                    <Route path="/:locale" element={<LocaleRootRedirect />} />
                    <Route path="/:locale/dashboard" element={<LocaleRoute><Dashboard profile={profile} onUpdate={onUpdate} /></LocaleRoute>} />
                    <Route path="/:locale/hideout" element={<LocaleRoute><Hideout profile={profile} onUpdate={onUpdate} /></LocaleRoute>} />
                    <Route path="/:locale/quests" element={<LocaleRoute><Quests profile={profile} onUpdate={onUpdate} /></LocaleRoute>} />
                    <Route path="/:locale/kappa-items" element={<LocaleRoute><KappaItems profile={profile} onUpdate={onUpdate} /></LocaleRoute>} />
                    <Route path="/:locale/kappa-path" element={<LocaleRoute><KappaPath profile={profile} onUpdate={onUpdate} /></LocaleRoute>} />
                    <Route path="/:locale/story" element={<LocaleRoute><StoryGuide profile={profile} onUpdate={onUpdate} /></LocaleRoute>} />
                    <Route path="/:locale/story/:chapterId" element={<LocaleRoute><StoryGuide profile={profile} onUpdate={onUpdate} /></LocaleRoute>} />
                    <Route path="/:locale/quest/:questSlug" element={<LocaleRoute><QuestLanding /></LocaleRoute>} />
                    <Route path="/:locale/privacy-policy" element={<LocaleRoute><PrivacyPolicy /></LocaleRoute>} />
                    <Route path="/:locale/terms-of-service" element={<LocaleRoute><TermsOfService /></LocaleRoute>} />
                    <Route path="/:locale/about" element={<LocaleRoute><AboutUs /></LocaleRoute>} />
                    <Route path="/:locale/contact" element={<LocaleRoute><Contact /></LocaleRoute>} />

                    <Route path="/tr/panel" element={<LegacyStaticRedirect routeId="dashboard" language="tr" />} />
                    <Route path="/tr/gorev-rehberi" element={<LegacyStaticRedirect routeId="quests" language="tr" />} />
                    <Route path="/tr/kappa-esyalari" element={<LegacyStaticRedirect routeId="kappa-items" language="tr" />} />
                    <Route path="/tr/hideout-rehberi" element={<LegacyStaticRedirect routeId="hideout" language="tr" />} />
                    <Route path="/tr/kappa-rehberi" element={<LegacyStaticRedirect routeId="kappa-path" language="tr" />} />
                    <Route path="/tr/hikaye-rehberi" element={<LegacyStaticRedirect routeId="story" language="tr" />} />
                    <Route path="/tr/gorev/:questSlug" element={<LegacyQuestRedirect language="tr" />} />
                    <Route path="/tr/rehber/:chapterId" element={<LegacyStoryRedirect language="tr" />} />
                    <Route path="/:locale/guide/:chapterId" element={<LegacyStoryRedirect />} />

                    <Route path="*" element={<FallbackRedirect />} />
                  </Routes>
                </div>
                {profile && <Footer />}
              </div>
            </div>
          </div>

          {!profile && !isPublicLegalPage && (
            <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 animate-fadeIn">
              <Welcome onComplete={onWelcomeComplete} />
            </div>
          )}
        </div>
      </DataProvider>
    </LanguageProvider>
  );
}

function App() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        try {
          const docRef = doc(db, 'users', user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            setProfile(normalizeProfile(docSnap.data()));
          } else {
            const localData = localStorage.getItem('tarkovProfile');
            if (localData) {
              const parsedData = normalizeProfile(JSON.parse(localData));
              await setDoc(docRef, parsedData);
              setProfile(parsedData);
              localStorage.removeItem('tarkovProfile');
            } else {
              setProfile(null);
            }
          }
        } catch (error) {
          console.error("Veri çekme hatası:", error);
        }
      } else {
        // ÇÖZÜM: Kullanıcı misafirse doğrudan null yapma, önce LocalStorage'a bak
        const localData = localStorage.getItem('tarkovProfile');
        if (localData) {
          const parsedData = normalizeProfile(JSON.parse(localData));
          localStorage.setItem('tarkovProfile', JSON.stringify(parsedData));
          setProfile(parsedData);
        } else {
          setProfile(null);
        }
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const handleProfileUpdate = async (updatedProfile) => {
    const normalizedProfile = normalizeProfile(updatedProfile);
    setProfile(normalizedProfile);
    if (currentUser) {
      try {
        await setDoc(doc(db, 'users', currentUser.uid), normalizedProfile, { merge: true });
      } catch (error) {
        console.error("Buluta kaydetme hatası:", error);
      }
    } else {
      localStorage.setItem('tarkovProfile', JSON.stringify(normalizedProfile));
    }
  };

  const handleWelcomeComplete = async (newProfile) => {
    const normalizedProfile = normalizeProfile(newProfile);
    setProfile(normalizedProfile);
    if (currentUser) {
      try {
        await setDoc(doc(db, 'users', currentUser.uid), normalizedProfile);
      } catch (error) {
        console.error("Buluta profil oluşturma hatası:", error);
      }
    } else {
      localStorage.setItem('tarkovProfile', JSON.stringify(normalizedProfile));
    }
  };

  const handleReset = async () => {
    if (window.confirm('Are you sure you want to reset your profile? This cannot be undone.')) {
      if (currentUser) {
        await deleteDoc(doc(db, 'users', currentUser.uid));
      }
      localStorage.removeItem('tarkovProfile');
      localStorage.removeItem('story_guide_progress');
      window.location.reload();
    }
  };

  const getBackgroundClass = () => {
    if (!profile?.settings?.background) return 'bg-gradient-to-br from-[#0B0E14] to-[#1a1f2e]';
    const bg = profile.settings.background;
    if (bg.startsWith('bg-')) return bg;
    const bgMap = {
      'default': 'bg-gradient-to-br from-[#0B0E14] to-[#1a1f2e]',
      'green': 'bg-gradient-to-br from-gray-950 via-emerald-950 to-gray-950',
      'blue': 'bg-gradient-to-br from-gray-950 via-blue-950 to-gray-950',
      'purple': 'bg-gradient-to-br from-gray-950 via-purple-950 to-gray-950',
    };
    return bgMap[bg] || bgMap['default'];
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0B0E14] flex items-center justify-center text-white animate-pulse">Loading App...</div>;
  }

  return (
    <Router>
      <AppShell
        profile={profile}
        onUpdate={handleProfileUpdate}
        onWelcomeComplete={handleWelcomeComplete}
        onReset={handleReset}
        backgroundClass={getBackgroundClass()}
      />
    </Router>
  );
}

export default App;
